import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Shield, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import { useTranslation } from '../../context/LanguageContext';
import { createPayment, getPlansForProfileType } from '../../services/paymentService';

const PaymentModal = ({ isOpen, onClose, planType, user }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [plan, setPlan] = useState(null);

    useEffect(() => {
        const loadPlan = async () => {
            if (isOpen && planType) {
                const plans = await getPlansForProfileType();
                setPlan(plans[planType.toUpperCase()] || null);
            }
        };
        loadPlan();
    }, [isOpen, planType]);

    if (!plan) return null;

    const handlePayment = async () => {
        if (!user?.peopleUuid) {
            setError('Vous devez être connecté pour souscrire à un abonnement.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await createPayment(user.peopleUuid, planType);

            if (result.success && result.data?.paymentUrl) {
                // Redirect to Mollie checkout
                window.location.href = result.data.paymentUrl;
            } else {
                setError(result.message || 'Erreur lors de la création du paiement.');
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Abonnement ${plan.name}`}
            subtitle={plan.description}
            size="sm"
            hideFooter
        >
            <div className="p-6">
                {/* Prix */}
                <div className="text-center mb-6">
                    <span className="text-4xl font-bold text-gray-900">{plan.price || 0}€</span>
                    <span className="text-gray-500">{plan.period || '/mois'}</span>
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>
                )}

                {/* Bouton paiement */}
                <button
                    onClick={handlePayment}
                    disabled={isLoading}
                    className="w-full bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white py-3 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" />Redirection vers le paiement...</>
                    ) : (
                        <><CreditCard className="w-5 h-5" />Payer {plan.price}€</>
                    )}
                </button>

                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
                    <Shield className="w-4 h-4" />
                    <span>Paiement sécurisé par Mollie</span>
                </div>

                <div className="flex items-center justify-center gap-3 mt-4">
                    <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-6" />
                    <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Credit Card" className="h-6" />
                    <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-6" />
                    <img src="https://www.mollie.com/external/icons/payment-methods/paypal.svg" alt="PayPal" className="h-6" />
                </div>

                {/*
                    Les deux liens visaient /conditions et /confidentialite, qui
                    n'existent pas : le catch-all de App.jsx renvoyait donc vers
                    l'accueil. Le client « acceptait » des conditions qu'il lui
                    etait impossible de lire au moment de payer.

                    Link et non <a> : un href rechargeait toute l'application en
                    plein tunnel de paiement. target="_blank" garde le tunnel
                    ouvert derriere la lecture.
                */}
                <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500">
                        {t.payment?.acceptAvant}
                        <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-cabaza-purple hover:underline">
                            {t.payment?.acceptTermes}
                        </Link>
                        {t.payment?.acceptMilieu}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-cabaza-purple hover:underline">
                            {t.payment?.acceptConfid}
                        </Link>
                        {t.payment?.acceptFin}
                    </p>
                </div>
            </div>
        </Modal>
    );
};

export default PaymentModal;
